/**
 * MessageAttachmentList Component
 * 
 * Displays a collection of file attachments associated with a user message.
 * Shows files in a clean, horizontal layout within the message context.
 * 
 * Features:
 * - Horizontal layout for multiple file attachments
 * - Responsive wrapping for many files
 * - Clean integration with message flow
 * - Optional file action handling
 */

import React from 'react';
import { Group, Text, Box } from '@mantine/core';
import { MessageAttachmentItem } from '../MessageAttachmentItem';
import classes from './MessageAttachmentList.module.css';

// =============================================================================
// TYPES
// =============================================================================

export interface MessageAttachmentListProps {
  /** Array of filenames attached to the message */
  files: string[];
  /** The message ID these files are associated with */
  messageId: string;
  /** Whether the list is read-only (default: true) */
  readOnly?: boolean;
  /** Optional callback for file actions */
  onFileAction?: (action: string, messageId: string, filename: string) => void;
  /** Show file count label */
  showCount?: boolean;
  /** Additional CSS class names */
  className?: string;
  /** Inline styles */
  style?: React.CSSProperties;
}


// =============================================================================
// MAIN COMPONENT
// =============================================================================

export const MessageAttachmentList: React.FC<MessageAttachmentListProps> = ({
  files,
  messageId,
  readOnly = true,
  onFileAction,
  showCount = true,
  className,
  style,
}) => {
  // =============================================================================
  // EARLY RETURN
  // =============================================================================
  
  if (!files || files.length === 0) {
    return null;
  }

  // =============================================================================
  // RENDER
  // =============================================================================
  
  return (
    <Box className={`${classes.attachmentList} ${className || ''}`} style={style}>
      <Box className={classes.attachmentContainer}>
        {/* Optional file count label */}
        {showCount && (
          <Text size="xs" c="dimmed" className={classes.attachmentLabel}>
            Attached files ({files.length})
          </Text>
        )}
        
        {/* File items container - no dynamic width constraint */}
        <Box className={classes.attachmentItems}>
          {files.map((filename) => (
            <MessageAttachmentItem
              key={`${messageId}-${filename}`}
              filename={filename}
              messageId={messageId}
              readOnly={readOnly}
              onAction={onFileAction}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default MessageAttachmentList;